// src/lib/financeiro/baseDataFatura.ts
//
// Qual data da fatura o Painel Executivo usa para recortar período e para
// montar a série mensal.
//
// Três perguntas diferentes, que o mesmo período responde de formas diferentes:
//   REFERÊNCIA — "o consumo de qual mês?" (o padrão, e como o painel sempre foi)
//   VENCIMENTO — "o que vence neste mês?" (é o eixo do caixa)
//   EMISSÃO    — "o que foi emitido neste mês?" (é o eixo da operação de cobrança)
//
// Medido em produção (16/09/2026) sobre as 6.144 faturas com boleto: referência
// e vencimento existem em 100% delas; a data de emissão, somando
// `boleto_emissao_data` com `emitido_em`, cobre 98,5%.
//
// ⚠️ O FILTRO e a SÉRIE MENSAL têm de usar a MESMA base. Enquanto o filtro
// recortava por vencimento e os gráficos agrupavam por referência, o mini-card
// "Evolução Financeira Mensal" mostrava "emitido em NOV/2026" com setembro
// selecionado: duas parcelas de uma renegociação (referência OUT e NOV/2026,
// R$ 1.539,85) vencem em setembro e viravam o último ponto da série.
//
// Módulo puro, sem I/O nem JSX: roda sob `node --test` com type-stripping.

import type { FaturaPainel } from './types.ts';
import type { JanelaPeriodo } from './periodo.ts';

export type BaseDataFinanceiro = 'referencia' | 'vencimento' | 'emissao';

export const ROTULOS_BASE_DATA: Record<BaseDataFinanceiro, string> = {
  referencia: 'Mês de referência',
  vencimento: 'Mês de vencimento',
  emissao: 'Mês de emissão',
};

/** O que cada base responde — vai no texto de ajuda do seletor. */
export const DESCRICOES_BASE_DATA: Record<BaseDataFinanceiro, string> = {
  referencia: 'O mês de consumo da fatura. É como o painel sempre contou.',
  vencimento: 'O mês em que o boleto vence — o eixo do caixa.',
  emissao: 'O mês em que o boleto foi emitido. 1,5% das faturas não têm essa data e ficam de fora.',
};

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** Rótulo do balde quando a fatura não tem a data da base escolhida. */
export const SEM_DATA = 'Sem data';

/**
 * A data da fatura na base escolhida, em ISO (`YYYY-MM-DD`). `null` quando a
 * fatura não tem essa data, e sempre `null` na base referência — ali o mês não
 * vem de uma data, vem de `mes_referencia`.
 *
 * Vencimento segue `vencimento_boleto || data_vencimento`, o mesmo par que o
 * resto do sistema usa (`dataVencimentoDe`). Emissão prefere
 * `boleto_emissao_data`, que é a data do boleto em si e está preenchida em mais
 * faturas (5.983 contra 5.623), e usa `emitido_em` como reserva.
 */
export function dataDaBase(f: FaturaPainel, base: BaseDataFinanceiro): string | null {
  if (base === 'vencimento') return iso(f.vencimento_boleto || f.data_vencimento);
  if (base === 'emissao') return iso(f.boleto_emissao_data || f.emitido_em);
  return null;
}

const iso = (v: string | null | undefined): string | null => {
  const s = String(v ?? '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

/** "SET/2026" — o mês da fatura NA BASE ESCOLHIDA. É o balde da série mensal. */
export function mesDaBase(f: FaturaPainel, base: BaseDataFinanceiro): string {
  if (base === 'referencia') return f.mes_referencia || SEM_DATA;
  const data = dataDaBase(f, base);
  if (!data) return SEM_DATA;
  const mes = Number(data.slice(5, 7));
  return mes >= 1 && mes <= 12 ? `${MESES[mes - 1]}/${data.slice(0, 4)}` : SEM_DATA;
}

/** A data ISO cai dentro da janela de meses? Compara MÊS, não dia. */
export function dataNaJanela(isoData: string | null, janela: JanelaPeriodo | null): boolean {
  if (!janela) return true;
  if (!isoData) return false;
  const ano = Number(isoData.slice(0, 4));
  const mes = Number(isoData.slice(5, 7));
  const d = new Date(ano, mes - 1, 1).getTime();
  return d >= janela.inicio.getTime() && d <= janela.fim.getTime();
}
